/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/jsx-filename-extension */
import { Stack, SearchBox, Pivot, PivotItem, GroupedList, assertNever, GroupHeader, IGroup, SelectionMode, FocusZoneTabbableElements, IGroupHeaderProps, TooltipHost } from '@fluentui/react';
import React, { FC, useContext, useState, } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { CategoryDescriptor, CandidateItem, ItemState, isLockedCandidateItem } from './types';
import { IconName, ItemRow } from './ItemRow';
import { itemAlignmentStackTokensGap } from './utils/style';

interface AvailableColumnsProps {
  headerName: string;
  categories: CategoryDescriptor[];
  onAddItems: (newSelectedKeys: CandidateItem[]) => void;
  onItemOperationClick: (item?: CandidateItem) => void;
  onExpandAllClick: () => void;
  onCollapseAllClick: () => void;
  onToggleCollapse: (triggerGroup: IGroup) => void;
}

export const AvailableColumns: FC<AvailableColumnsProps> = (props) => {
  const { headerName, categories, onItemOperationClick, onCollapseAllClick, onExpandAllClick, onToggleCollapse, onAddItems } = props;
  const [searchKey, setSearchkey] = useState<string | undefined>(undefined);

  const i18n: any = useContext(I18nContext);
  const columnNameText: string = i18n.getString(_TL_('Column name'));
  const expandAllText: string = i18n.getString(_TL_('Expand all'));
  const collapseAllText: string = i18n.getString(_TL_('Collapse all'));
  const addAllText: string = i18n.getString(_TL_('Add all'));
  const addText: string = i18n.getString(_TL_('Add'));
  const addedText: string = i18n.getString(_TL_('Added'));

  const filteredCategories = categories.map(category => filterCategory(category, searchKey));

  return (
    <div className="list-wrapper candidates-list">
      <div className="list-header"> {headerName}</div>
      <Stack className="action-group" horizontal tokens={itemAlignmentStackTokensGap}>
        <span className="operation enabled" tabIndex={0} onClick={onExpandAllClick} data-testId="expand-all-button">
          {expandAllText}
        </span>
        <span className="operation enabled" tabIndex={0} onClick={onCollapseAllClick} data-testId="collapse-all-button">
          {collapseAllText}
        </span>
        <span className="operation enabled" tabIndex={0} onClick={onAddAllClick} data-testId="add-all-button">
          {addAllText}
        </span>
      </Stack>
      <SearchBox
        placeholder={columnNameText}
        onChange={onSearchValueChange}
      />
      <Pivot className="categories-wrapper">
        {filteredCategories.map((category) => {
          const itemCountsOfAllCategory = category.groups.reduce((pre, cur) => pre + cur.items.length, 0);
          return (
            <PivotItem key={category.key} className="groups-wrapper" headerText={`${category.headerName} (${itemCountsOfAllCategory})`} >
              {category.groups.map((group) => {
                const groupOfGroupedList: IGroup = {
                  count: group.items.length,
                  key: group.key,
                  name: group.name,
                  startIndex: 0,
                  isCollapsed: !!group.isCollapsed,

                };
                // We use many simple `GroupedList`s rather than a complex `GroupedList`.
                return (<GroupedList
                  className="item-list"
                  groupProps={{
                    showEmptyGroups: true,
                    headerProps: { onToggleCollapse, onGroupHeaderClick: onToggleCollapse },
                    onRenderHeader: onRenderCandidateGroupHeader
                  }}
                  key={group.key}
                  selectionMode={SelectionMode.none}
                  groups={[groupOfGroupedList]}
                  items={group.items}
                  focusZoneProps={{
                    handleTabKey: FocusZoneTabbableElements.all,
                    preventFocusRestoration: true,
                  }}
                  onRenderCell={onRenderCandidateCell}
                />);
              })}
            </PivotItem>
          );
        })}
      </Pivot>
    </div>
  );

  function filterCategory(category: CategoryDescriptor, searchString?: string): CategoryDescriptor {
    if (!searchString) {
      return category;
    }
    const newGroups = category.groups.map(group => ({
        ...group,
        items: group.items.filter(item =>
          item.friendlyName.toLowerCase().includes(searchString.toLowerCase())),
      }));
    return {
      ...category,
      groups: newGroups,
    }
  }

  function onSearchValueChange(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) {
    setSearchkey(newValue);
  }

  function onRenderCandidateCell(nestingDepth?: number, item?: CandidateItem, itemIndex?: number): React.ReactNode {
    if (!item) {
      throw new Error("unknown item");
    }
    if (itemIndex === undefined) {
      throw new Error("unknown itemIndex, find the reason rather than just use it.");
    }
    const isMandatory = item.state === ItemState.mandatory;

    const row = (
      <ItemRow
        index={itemIndex}
        text={item?.friendlyName ?? ''}
        iconName={isMandatory ? IconName.Empty : IconName.GripperDotsVertical}
        isOperationEnabled={getIsOperationEnabled(item)}
        operationText={getOperationText(item)}
        data={item}
        onOperationTrigger={onItemOperationClick}
      />);
    // eslint-disable-next-line no-undef
    let message: string | JSX.Element | undefined;

    if (isLockedCandidateItem(item)) {
      message = item.lockReason;
    }

    return withTooltip(row, message);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    function getOperationText(item: CandidateItem) {
      return item.isSelected ? addedText : addText;
    }

    // eslint-disable-next-line no-undef, @typescript-eslint/no-shadow
    function withTooltip(element: JSX.Element, message?: string | JSX.Element) {
      if (!message) { return element; }

      return (
        <TooltipHost
          content={message}
        >
          {element}
        </TooltipHost>
      )
    }
  }

  function getIsOperationEnabled(item: CandidateItem) {
    switch (item.state) {
      case ItemState.mandatory:
      case ItemState.lock:
      case ItemState.unknown:
        return false;
      case ItemState.optional:
        return !item.isSelected;
      default:
        return assertNever(item.state);
    }
  }

  // eslint-disable-next-line no-undef, @typescript-eslint/no-shadow
  function onRenderCandidateGroupHeader(props?: IGroupHeaderProps): JSX.Element {
    return (<div className="wrapped-group-header">
      <GroupHeader {...props} />
      <span className="group-operation" onClick={() => onAddGroupAllClick(props.group.key)} data-testId="group-add-all-button">
        {addAllText}
      </span>
            </div>)
  }

  function onAddAllClick() {
    const itmesToAdd: CandidateItem[] = []
    categories.forEach((category) => {
      category.groups.forEach((group) => {
        group.items.forEach((item) => {
          // Could not use only `getIsOperationEnabled` to limit candidate items, because "onAddItems" callback might remove some selected columns.
          itmesToAdd.push(item)
        })
      })
    })
    onAddItems(itmesToAdd)
  }

  function onAddGroupAllClick(groupKey) {
    const itmesToAdd: CandidateItem[] = []
    categories.forEach((category) => {
      category.groups.forEach((group) => {
        if (group.key === groupKey) {
          group.items.forEach((item) => {
            if (getIsOperationEnabled(item)) {
              itmesToAdd.push(item)
            }
          })
        }
      })
    })
    onAddItems(itmesToAdd)
  }
}
